import React, {useEffect, useState} from "react";
import RightScrollable from "../../../components/shared/RightScrollable";
import {getShopName, JURISDICTIONS} from "../../../config";
import Card from "./Card";
import GraphPanel from "./GraphPanel";
import {Label, ReferenceLine} from "recharts";
import Button from "../../../components/Button";
import styles from "./ReportsDashboard.module.scss";
import monthlyData from "./data/monthly/data";
import {FILTERS} from "./data/filters";
import {TYPES} from "./data/types";
import {jurisdictionsDataFor, ordersDataFor, revenuesDataFor} from "./dataHelpers";
import JurisdictionSelector from "./JurisdictionSelector";


 
function yearlyReferenceLinesForGraph(graphData) {
  if (!graphData) {
    return null;
  }
  // TODO only for full history + dates as x keys
  const referenceLines = [];

  let previousYear = "";
  graphData.forEach((data, index) => {
    const year = data.name.split("-")[0];
    if (year !== previousYear) {
      referenceLines.push(
        <ReferenceLine key={index} x={index} stroke="green"
          position="start" // left of the bar itself
        >
          {/* Place label at the top right of the line */}
          <Label value={year} position="insideTopLeft"/> 
        </ReferenceLine>
      );
    }
    previousYear = year;
  });
  
  return referenceLines;
}

const DEFAULT_TYPE = TYPES.SUBS;

export default function ReportsDashboard(props) {
  // For now we only have data per month
  const [fullDataMap] = useState(monthlyData.data);
  const [filteredData, setFilteredData] = useState([]);
  const [previousExerciseData, setPreviousExerciseData] = useState([]); // To compute relative changes
  const [currentFilter, setCurrentFilter] = useState(FILTERS.YEARLY);
  const [currentType, setCurrentType] = useState(DEFAULT_TYPE);

  useEffect(() => {
    const fullDataArray = fullDataMap.toJSON();
    setFilteredData(currentFilter.filter(fullDataArray));
    setPreviousExerciseData(currentFilter.filterPreviousExercise(fullDataArray));
  }, [fullDataMap, currentFilter.label, currentType.label]);

  return (
    <ReportsWithFilteredData
      data={filteredData}
      previousExerciseData={previousExerciseData}
      currentFilter={currentFilter} setCurrentFilter={setCurrentFilter}
      currentType={currentType} setCurrentType={setCurrentType} />
  );
}

function ReportsWithFilteredData(props) {
  const {
    data,
    previousExerciseData,
    currentFilter = FILTERS.YEARLY,
    setCurrentFilter,
    currentType = TYPES.ORDERS,
    setCurrentType,
  } = props;

  // From the data we extract the graphData (could be revenue, clients, ...)
  const [graphData, setGraphData] = useState();
  const [customElements, setCustomElements] = useState();

  const [jurisdictionsCards, setJurisdictionsCards] = useState([]);
  const [currentJurisdiction, setCurrentJurisdiction] = useState(null); // No filter
  const [revenuesCards, setRevenuesCards] = useState([]);
  const [ordersCards, setOrdersCards] = useState([]);
  // const [clientsCards, setClientsCards] = useState([]);

  useEffect(() => {
    // Extract graph data anytime the data itself, the filter or type of data changes
    let gData = data.map((x) => {
      const [date, value] = x;
  
      const keys = Object.keys(value);
      const result = {
        name: date,
      }
      
      keys.forEach(k => result[k] = currentType.dataFetcher(value[k]));
      return result;
    });

    setGraphData(gData);
  }, [data, currentFilter.label, currentType.label]);

  useEffect(() => {
    const toCard = (data, customProps) => data.map((datum) => <Card {...customProps} {...datum} key={datum.name} />)

    setJurisdictionsCards(toCard(jurisdictionsDataFor(data, previousExerciseData), {
      bolder: true,
      currentShopId: currentJurisdiction,
      onClick: setCurrentJurisdiction,
    }));
    setRevenuesCards(toCard(revenuesDataFor(data, previousExerciseData, currentJurisdiction)));
    setOrdersCards(toCard(ordersDataFor(data, previousExerciseData, currentJurisdiction)));
    // setClientsCards(toCard(clientsDataFor(data, previousExerciseData, currentJurisdiction)));
  }, [data, previousExerciseData, currentJurisdiction, currentFilter.label, currentType.label]);

  useEffect(() => {
    if (currentJurisdiction === null) {
      // Make sure the current type is OK
      if (!currentType.visibleFor(null)) {
        setCurrentType(DEFAULT_TYPE);
      }
    }
  }, [currentJurisdiction])

  useEffect(() => {
    setCustomElements(yearlyReferenceLinesForGraph(graphData))
  }, [graphData]);

  const selectedJurisdictions = currentJurisdiction ? [currentJurisdiction] : Object.values(JURISDICTIONS);

  const jurisdictionColor = (jurisdiction) => {
    let color = "";

    switch (jurisdiction) {
      case JURISDICTIONS.USA:
        color = "#060E45"
        break;
      case JURISDICTIONS.CANADA:
        color = "#D62718";
        break;
      case JURISDICTIONS.EU:
        color = "#002F9A";
        break;
      case JURISDICTIONS.SWITZERLAND:
        color = "#FFD82C";
        break;
      case JURISDICTIONS.UK:
        color = "#007D34";
        break;
      case JURISDICTIONS.SG:
        color = "#F36D26";
        break;
      
      default:
        color = "#eee";
        break;
    }

    return color;
  }

  const filters = Object.values(FILTERS).map((v) =>
    <Button key={v.label} label={v.label} active={!!v.filter}
      onClick={() => setCurrentFilter(v)}
      primary={v.label === currentFilter.label}
    />
  );

  return (
    <div className={styles.module}>
      <div className={styles.header}>
        <div className={styles.filters}>
          {filters}
        </div>
        <JurisdictionSelector
          currentJurisdiction={currentJurisdiction}
          setCurrentJurisdiction={setCurrentJurisdiction}
        />
      </div>

      <div className={styles.panel}>
        <h1>Jurisdictions <small>Click to select</small></h1>
        <RightScrollable
          className={styles.cards}
          style={{"--mobile-width": "calc(100vw - var(--sg-spacing-big) - 15px)"}}
        >
          {jurisdictionsCards}
        </RightScrollable>
      </div>

      <GraphPanel data={graphData} keys={selectedJurisdictions}
        dataColor={jurisdictionColor} dataLabel={getShopName}
        getXAxisLabel={(data) => monthlyData.getXAxisLabel(data, currentFilter)}
        customElements={customElements}
        currentJurisdiction={currentJurisdiction}
        dataTypes={TYPES}
        currentDataType={currentType}
        setCurrentDataType={setCurrentType}
      />

      { revenuesCards.length > 0 &&
        <div className={styles.panel}>
          <h1>Revenues</h1>
          <RightScrollable
            className={styles.cards}
            style={{"--mobile-width": "calc(100vw - var(--sg-spacing-big) - 15px)"}}
          >
            {revenuesCards}
          </RightScrollable>
        </div>
      }
      <div className={styles.panel}>
        <h1>Orders</h1>
        <RightScrollable
          className={styles.cards}
          style={{"--mobile-width": "calc(100vw - var(--sg-spacing-big) - 15px)"}}
        >
          {ordersCards}
        </RightScrollable>
      </div>
    </div>
  );
}
